// Voice capture for the Ask Cannafax composer: records a complete WAV (decodable
// on every browser, incl. iOS Safari) and streams back a transcript.
import { supabase } from '@/integrations/supabase/client';

function encodeWav(chunks: readonly Float32Array[], sampleRate: number): Blob {
  const length = chunks.reduce((sum, c) => sum + c.length, 0);
  const bytes = new ArrayBuffer(44 + length * 2);
  const view = new DataView(bytes);
  const tag = (o: number, v: string) => { for (let i = 0; i < v.length; i++) view.setUint8(o + i, v.charCodeAt(i)); };
  tag(0, 'RIFF'); view.setUint32(4, 36 + length * 2, true); tag(8, 'WAVE'); tag(12, 'fmt ');
  view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true); view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true); view.setUint16(34, 16, true); tag(36, 'data'); view.setUint32(40, length * 2, true);
  let offset = 44;
  for (const chunk of chunks) for (const value of chunk) {
    const s = Math.max(-1, Math.min(1, value));
    view.setInt16(offset, s * (s < 0 ? 32768 : 32767), true); offset += 2;
  }
  return new Blob([bytes], { type: 'audio/wav' });
}

export interface Recording {
  stop: () => Promise<File>;
  cancel: () => void;
  /** Current microphone loudness, 0 (silent) to 1 (loud). Local only; never leaves the device. */
  level: () => number;
}

export async function startRecording(): Promise<Recording> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  let context: AudioContext | undefined;
  try {
    context = new AudioContext();
    await context.resume();
    const ctx = context;
    const source = ctx.createMediaStreamSource(stream);
    const node = ctx.createScriptProcessor(4096, 1, 1);
    const chunks: Float32Array[] = [];
    node.onaudioprocess = (e) => chunks.push(new Float32Array(e.inputBuffer.getChannelData(0)));
    source.connect(node); node.connect(ctx.destination);
    // Local loudness meter for the "Listening…" indicator. Analysis happens
    // entirely on-device; the audio itself is only uploaded on stop().
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.6;
    source.connect(analyser);
    const spectrum = new Uint8Array(analyser.frequencyBinCount);
    let done = false;
    const teardown = () => {
      done = true;
      stream.getTracks().forEach((t) => t.stop());
      node.onaudioprocess = null; node.disconnect(); source.disconnect(); analyser.disconnect();
      void ctx.close();
    };
    return {
      level() {
        if (done) return 0;
        analyser.getByteTimeDomainData(spectrum);
        let peak = 0;
        for (const v of spectrum) peak = Math.max(peak, Math.abs(v - 128) / 128);
        return Math.min(1, peak * 1.6);
      },
      async stop() {
        if (done) throw new Error('Recording already stopped');
        const rate = ctx.sampleRate;
        teardown();
        const blob = encodeWav(chunks, rate);
        if (blob.size < 2048) throw new Error("We didn't catch anything. Please try again.");
        return new File([blob], 'recording.wav', { type: 'audio/wav' });
      },
      cancel() { if (!done) teardown(); },
    };
  } catch (err) {
    stream.getTracks().forEach((t) => t.stop());
    await context?.close();
    throw err;
  }
}

/** Uploads a recording and calls onText with the transcript so far as it streams in. */
export async function transcribe(file: File, onText: (text: string) => void, signal?: AbortSignal): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Please sign in to use voice.');
  const form = new FormData();
  form.append('file', file, file.name);
  const base = import.meta.env.VITE_SUPABASE_URL;
  const res = await fetch(`${base}/functions/v1/transcribe-voice`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.access_token}`, apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY },
    body: form,
    signal,
  });
  if (!res.ok || !res.body) {
    let message = "We couldn't transcribe that. Please try again.";
    try { const j = await res.json(); if (typeof j?.error === 'string') message = j.error; else if (typeof j?.error?.message === 'string') message = j.error.message; } catch { /* keep default */ }
    if (res.status === 429) message = 'Voice is busy right now. Please try again in a moment.';
    if (res.status === 402) message = 'Voice is temporarily unavailable.';
    throw new Error(message);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = ''; let text = ''; let final: string | null = null;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf('\n')) >= 0) {
      const line = buffer.slice(0, idx).trim(); buffer = buffer.slice(idx + 1);
      if (!line.startsWith('data:')) continue;
      const payload = line.slice(5).trim();
      if (!payload || payload === '[DONE]') continue;
      try {
        const evt = JSON.parse(payload);
        if (evt.type === 'transcript.text.delta' && typeof evt.delta === 'string') { text += evt.delta; onText(text); }
        else if (evt.type === 'transcript.text.done' && typeof evt.text === 'string') { final = evt.text; onText(final); }
        else if (evt.type === 'error' || evt.error) throw new Error("We couldn't transcribe that. Please try again.");
      } catch (e) { if (e instanceof Error && e.message.startsWith('We couldn')) throw e; }
    }
  }
  const result = (final ?? text).trim();
  if (!result) throw new Error("We didn't catch anything. Please try again.");
  return result;
}
